/**
 * 로그인 중인 동안 세션이 아직 유효한지 주기적으로 확인하는 훅.
 *
 * 15초마다 checkSession(`/app/auths/check-session`)을 호출해 sessionStorage의
 * accessToken이 서버에서도 여전히 유효한지 확인하고, 실패가 이어지면 네트워크 상태를
 * unstable → offline로 낮춰 화면에 표시한다(useNetworkStatus 갱신).
 * accessToken 만료 시 401 응답을 받아 갱신을 시도하고, 갱신도 실패하면 /login으로 보내는
 * 흐름은 `pages/api/axiosInstance.ts`의 응답 인터셉터가 별도로 처리한다.
 * 이 훅은 그와 별개로 "세션이 지금도 유효한가"를 주기적으로 재확인해 화면 상태에 반영할 뿐이다.
 */
import { useEffect, useRef } from "react";
import { checkSession } from "@/api/auth-custom";
import { useLogout } from "./useLogout";
import { useNetworkStatus } from "./useNetworkStatus";

const NORMAL_INTERVAL = 15 * 1000; // 15초
const RETRY_INTERVAL = 5 * 1000;  // 실패 시 5초

export const useSessionHeartbeat = () => {
    const logout = useLogout();
    const { updateStatus } = useNetworkStatus();
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const failureCountRef = useRef(0);

    useEffect(() => {
        const runHeartbeat = async () => {
            const accessToken = sessionStorage.getItem("accessToken");
            if (!accessToken) {
                // 로그아웃했거나 세션이 끊겨 로그인 화면으로 돌아온 직후다.
                // ★ 여기서 그냥 빠져나가면 두 가지가 망가진다.
                //   1. 직전에 offline 이었으면 "네트워크 연결이 끊겼습니다" 띠가 로그인 화면까지 남는다.
                //      토큰이 없는 동안에는 상태를 normal 로 되돌릴 다른 주체가 없다.
                //   2. 재예약을 걸지 않으면 타이머가 죽어, 다시 로그인해도 세션 확인이 살아나지 않는다.
                //      이 useEffect 는 로그인 여부를 의존성으로 갖지 않아 다시 실행되지 않기 때문이다.
                failureCountRef.current = 0;
                updateStatus("normal");
                scheduleNext(NORMAL_INTERVAL);
                return;
            }

            try {
                await checkSession();
                
                // 성공 시
                failureCountRef.current = 0;
                updateStatus("normal");
                
                // 다음 실행 예약 (정상 간격)
                scheduleNext(NORMAL_INTERVAL);
            } catch (error: any) {
                // ★ 서버가 상태 코드를 돌려줬다면 통신은 성공한 것이다. 네트워크 실패로 세지 않는다.
                // 401(세션 만료·다른 기기 로그인)을 오프라인으로 세면 통신이 멀쩡한데도
                // "네트워크 연결이 끊겼습니다" 띠가 뜬다. 로그인 화면에 옛 토큰이 남아 있을 때
                // 이 경로로 401 이 반복되며 띠가 계속 떠 있었다.
                // 세션이 끝난 뒤의 처리(토큰 갱신·로그인 화면 이동)는 axiosInstance 응답 인터셉터 담당이다.
                if (error?.response?.status) {
                    failureCountRef.current = 0;
                    updateStatus("normal");
                    scheduleNext(NORMAL_INTERVAL);
                    return;
                }

                // 여기부터가 진짜 통신 실패다(응답 자체가 없음).
                console.error("Session heartbeat failed:", error);

                failureCountRef.current += 1;

                if (failureCountRef.current >= 3) {
                    updateStatus("offline");
                } else if (failureCountRef.current >= 2) {
                    updateStatus("unstable");
                }

                // 실패 시 다음 실행 예약 (단축된 간격)
                scheduleNext(RETRY_INTERVAL);
            }
        };

        const scheduleNext = (delay: number) => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
            timeoutRef.current = setTimeout(runHeartbeat, delay);
        };

        // 초기 실행
        runHeartbeat();

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, [logout, updateStatus]);
};
